import { FlexRowSmallSpacedCentered } from '@fox/layout/src/flex';
import MUIAlert from '@mui/material/Alert';
import MuiSnackbar, { SnackbarProps as MuiSnackbarProps } from '@mui/material/Snackbar';
import MuiSnackbarContent, { SnackbarContentProps as MuiSnackbarContentProps } from '@mui/material/SnackbarContent';
import { forwardRef, useState } from 'react';
import ReactDOM from 'react-dom';

export interface ISnackbarProps extends MuiSnackbarProps {
	severity?: 'success' | 'info' | 'warning' | 'error';
}

export type SnackbarState = Pick<ISnackbarProps, 'open' | 'message' | 'severity' | 'autoHideDuration'>;

export const MuiAlertSnackbar = (props: ISnackbarProps) => {
	return ReactDOM.createPortal(
		<Snackbar
			anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			autoHideDuration={props.autoHideDuration || 2500}
			onClose={props.onClose}
			{...props}
		>
			<MUIAlert sx={{ width: '100%', alignItems: 'center' }} severity={props.severity ?? 'info'}>
				<FlexRowSmallSpacedCentered>
					{props.message}
					{props.action}
				</FlexRowSmallSpacedCentered>
			</MUIAlert>
		</Snackbar>,
		document.body
	);
};

const DEFAULT_SNACKBAR_STATE: SnackbarState = {
	open: false,
	message: '',
	severity: 'success'
};

/**
 * Helper hook to setup snackbar state
 * Allows you to just set a message and it will automatically use success severity and show message (this can also be passed in when required)
 * @param initialState - Initial snackbar state - can be partial so you can just provide the auto hide delay
 * @returns
 */
export const useSnackbarAlert = (initialState?: Partial<SnackbarState>): [SnackbarState, (newState: Partial<SnackbarState>) => void] => {
	const [snackbar, _setSnackbar] = useState<SnackbarState>({ ...DEFAULT_SNACKBAR_STATE, ...(initialState ? initialState : {}) });
	const setSnackbar = (newState: Partial<SnackbarState>) => {
		_setSnackbar(prev => {
			// If we are closing we want to keep the previous state to avoid weird flashes of color/message change
			const state = newState.open === false ? { ...prev, open: false } : { ...DEFAULT_SNACKBAR_STATE, open: true, ...newState };
			return state;
		});
	};

	return [snackbar, setSnackbar];
};

export interface SnackbarProps extends MuiSnackbarProps {}

export const Snackbar = forwardRef((props: SnackbarProps, ref: React.Ref<unknown>) => {
	return <MuiSnackbar ref={ref} {...props} />;
});

export interface SnackbarContentProps extends MuiSnackbarContentProps {}

export const SnackbarContent = forwardRef((props: SnackbarContentProps, ref: React.Ref<HTMLDivElement>) => {
	return <MuiSnackbarContent ref={ref} {...props} />;
});
